import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { format, isFuture, parse, startOfDay } from 'date-fns';
import { de } from 'date-fns/locale';
import {
    ArrowLeft,
    CalendarIcon,
    Download,
    Loader2,
    Lock,
    LockOpen,
    Plus,
    Save,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useShallow } from 'zustand/react/shallow';
import { trpc } from '~/api/trpc';
import SignatureDialog from '~/components/cash-register/signature-dialog';
import Content from '~/components/layout/Content';
import LocalDateTime from '~/lib/localdatetime';
import { formatCurrency } from '~/lib/utils';
import { useUserStore } from '~/stores/user-store';
import { UserRole } from '~/types/server-types/UserRole';

// ============================================================================
// Constants
// ============================================================================

const DENOMINATIONS = [
    { key: 'count1ct', label: '1 Cent', valueEur: 0.01, group: 'coins' },
    { key: 'count2ct', label: '2 Cent', valueEur: 0.02, group: 'coins' },
    { key: 'count5ct', label: '5 Cent', valueEur: 0.05, group: 'coins' },
    { key: 'count10ct', label: '10 Cent', valueEur: 0.10, group: 'coins' },
    { key: 'count20ct', label: '20 Cent', valueEur: 0.20, group: 'coins' },
    { key: 'count50ct', label: '50 Cent', valueEur: 0.50, group: 'coins' },
    { key: 'count1eur', label: '1 EUR', valueEur: 1, group: 'coins' },
    { key: 'count2eur', label: '2 EUR', valueEur: 2, group: 'coins' },
    { key: 'count5eur', label: '5 EUR', valueEur: 5, group: 'bills' },
    { key: 'count10eur', label: '10 EUR', valueEur: 10, group: 'bills' },
    { key: 'count20eur', label: '20 EUR', valueEur: 20, group: 'bills' },
    { key: 'count50eur', label: '50 EUR', valueEur: 50, group: 'bills' },
    { key: 'count100eur', label: '100 EUR', valueEur: 100, group: 'bills' },
    { key: 'count200eur', label: '200 EUR', valueEur: 200, group: 'bills' },
    { key: 'count500eur', label: '500 EUR', valueEur: 500, group: 'bills' },
] as const;

type DenominationKey = (typeof DENOMINATIONS)[number]['key'];

type DenominationCounts = Record<DenominationKey, number>;

const DEFAULT_COUNTS: DenominationCounts = {
    count1ct: 0,
    count2ct: 0,
    count5ct: 0,
    count10ct: 0,
    count20ct: 0,
    count50ct: 0,
    count1eur: 0,
    count2eur: 0,
    count5eur: 0,
    count10eur: 0,
    count20eur: 0,
    count50eur: 0,
    count100eur: 0,
    count200eur: 0,
    count500eur: 0,
};


const getTodayString = () => LocalDateTime.now('Europe/Berlin').getDisplayDate();

const extractCounts = (v: Record<DenominationKey, number>): DenominationCounts => ({
    count1ct: v.count1ct,
    count2ct: v.count2ct,
    count5ct: v.count5ct,
    count10ct: v.count10ct,
    count20ct: v.count20ct,
    count50ct: v.count50ct,
    count1eur: v.count1eur,
    count2eur: v.count2eur,
    count5eur: v.count5eur,
    count10eur: v.count10eur,
    count20eur: v.count20eur,
    count50eur: v.count50eur,
    count100eur: v.count100eur,
    count200eur: v.count200eur,
    count500eur: v.count500eur,
});

const computeTotal = (counts: DenominationCounts) => {
    let totalCents = 0;
    for (const d of DENOMINATIONS) {
        totalCents += counts[d.key] * Math.round(d.valueEur * 100);
    }
    return totalCents / 100;
};

// ============================================================================
// Sub-components
// ============================================================================

type DenominationGridProps = {
    counts: DenominationCounts;
    onChange: (key: DenominationKey, value: number) => void;
    disabled?: boolean;
};

const DenominationGrid = ({ counts, onChange, disabled }: DenominationGridProps) => {
    const coins = DENOMINATIONS.filter((d) => d.group === 'coins');
    const bills = DENOMINATIONS.filter((d) => d.group === 'bills');

    const renderRow = (d: (typeof DENOMINATIONS)[number]) => {
        const count = counts[d.key];
        const subtotal = count * d.valueEur;

        return (
            <div key={d.key} className='flex items-center gap-3'>
                <span className='w-20 text-sm font-medium text-right'>{d.label}</span>
                <Input
                    type='number'
                    min={0}
                    value={count}
                    onChange={(e) => {
                        const val = parseInt(e.target.value, 10);
                        onChange(d.key, isNaN(val) || val < 0 ? 0 : val);
                    }}
                    className='w-24 text-center'
                    disabled={disabled}
                />
                <span className='text-sm text-muted-foreground font-mono w-28 text-right'>
                    = {formatCurrency(subtotal)}
                </span>
            </div>
        );
    };

    return (
        <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
            <div className='space-y-2'>
                <h4 className='text-sm font-semibold text-muted-foreground mb-3'>Münzen</h4>
                {coins.map(renderRow)}
            </div>
            <div className='space-y-2'>
                <h4 className='text-sm font-semibold text-muted-foreground mb-3'>Scheine</h4>
                {bills.map(renderRow)}
            </div>
        </div>
    );
};

// ============================================================================
// Main component
// ============================================================================

const CashRegisterStatement = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const [searchParams, setSearchParams] = useSearchParams();
    const trpcUtils = trpc.useUtils();

    const { role } = useUserStore(
        useShallow((state) => ({
            role: state.user?.role,
        })),
    );

    const isAdminOrOperator = role === UserRole.ADMIN || role === UserRole.OPERATOR;

    // Date state from URL query param, defaults to today
    const dateParam = searchParams.get('date');
    const selectedDate = dateParam && /^\d{4}-\d{2}-\d{2}$/.test(dateParam) ? dateParam : getTodayString();

    const setSelectedDate = (date: string) => {
        setSearchParams({ date }, { replace: true });
    };

    // View mode: 'new' = creating/editing a new version, 'viewing' = read-only view of a saved version
    const [viewMode, setViewMode] = useState<'new' | 'viewing'>('new');
    const [viewingVersionId, setViewingVersionId] = useState<string | null>(null);

    // Denomination counts state (for 'new' mode)
    const [counts, setCounts] = useState<DenominationCounts>({ ...DEFAULT_COUNTS });
    const [note, setNote] = useState('');
    const [showWarningDialog, setShowWarningDialog] = useState(false);
    const [showSignatureDialog, setShowSignatureDialog] = useState(false);
    const [showReopenDialog, setShowReopenDialog] = useState(false);
    const [pendingCloseAfterSave, setPendingCloseAfterSave] = useState(false);
    const [datePickerOpen, setDatePickerOpen] = useState(false);
    const [hasPreFilled, setHasPreFilled] = useState(false);

    // Fetch cash register info
    const { data: cashRegister, isLoading: registerLoading } = trpc.cashRegisters.getOne.useQuery(
        { id: id! },
        { enabled: !!id },
    );

    // Fetch statement for selected date
    const { data: statementData, isLoading: statementLoading } = trpc.cashRegisters.getStatement.useQuery(
        { cashRegisterId: id!, date: selectedDate },
        { enabled: !!id },
    );

    // Fetch all versions (admin only, for version selector)
    const { data: versions } = trpc.cashRegisters.getStatementVersions.useQuery(
        { statementId: statementData?.statementId! },
        { enabled: isAdminOrOperator && !!statementData?.statementId },
    );

    // The version currently being viewed (read-only)
    const viewingVersion = useMemo(() => {
        if (viewMode !== 'viewing' || !viewingVersionId || !versions) {
            return null;
        }
        return versions.find((v) => v.id === viewingVersionId) ?? null;
    }, [viewMode, viewingVersionId, versions]);

    // Counts to display: from viewing version if in view mode, otherwise from editable state
    const displayCounts: DenominationCounts = viewingVersion
        ? extractCounts(viewingVersion)
        : counts;

    // Pre-fill denomination counts from latest version when data loads
    useEffect(() => {
        if (statementData?.latestVersion && !hasPreFilled) {
            setCounts(extractCounts(statementData.latestVersion));
            setHasPreFilled(true);
        } else if (statementData && !statementData.latestVersion && !hasPreFilled) {
            setCounts({ ...DEFAULT_COUNTS });
            setHasPreFilled(true);
        }

        // When closed, auto-switch to viewing mode for the latest version
        if (statementData?.isClosed && statementData.latestVersion && viewMode === 'new') {
            setViewMode('viewing');
            setViewingVersionId(statementData.latestVersion.id);
        }
    }, [statementData, hasPreFilled]);

    // Reset state when date changes
    useEffect(() => {
        setHasPreFilled(false);
        setNote('');
        setViewMode('new');
        setViewingVersionId(null);
    }, [selectedDate]);

    // Live-compute totals
    const totalCountedEur = useMemo(() => computeTotal(displayCounts), [displayCounts]);

    const expectedBalanceEur = viewingVersion
        ? viewingVersion.expectedBalanceEur
        : (statementData?.expectedBalanceEur ?? 0);

    const differenceEur = viewingVersion
        ? viewingVersion.differenceEur
        : Math.round((totalCountedEur - expectedBalanceEur) * 100) / 100;

    // Save mutation
    const saveMutation = trpc.cashRegisters.saveStatementVersion.useMutation({
        onSuccess: async (_data, variables) => {
            if (variables.closeAfterSave) {
                toast.success('Kassensturz gespeichert und Tagesabschluss durchgeführt');
            } else {
                toast.success('Kassensturz gespeichert');
            }

            // Await refetch so we get the fresh data before updating local state
            const freshStatement = await trpcUtils.cashRegisters.getStatement.fetch(
                { cashRegisterId: id!, date: selectedDate },
            );

            // Invalidate related queries in parallel (no need to await these)
            trpcUtils.cashRegisters.getStatementVersions.invalidate();
            trpcUtils.cashRegisters.getStatementOverview.invalidate();
            trpcUtils.cashRegisters.getTodayStatementStatus.invalidate();

            // Pre-fill counts from the freshly saved version
            if (freshStatement?.latestVersion) {
                setCounts(extractCounts(freshStatement.latestVersion));
            }
            setHasPreFilled(true);
            setNote('');
            setPendingCloseAfterSave(false);
            setViewMode('new');
            setViewingVersionId(null);
        },
        onError: (error) => {
            setPendingCloseAfterSave(false);
            toast.error(`Fehler beim Speichern: ${error.message}`);
        },
    });

    // Download mutations
    const kassenberichtDownload = trpc.cashRegisters.getCashStatementKassenberichtUrl.useMutation({
        onSuccess: (data) => {
            window.open(data.downloadUrl, '_blank');
        },
        onError: (error) => {
            toast.error(`Fehler beim Herunterladen: ${error.message}`);
        },
    });

    const anlageDownload = trpc.cashRegisters.getCashStatementAnlageUrl.useMutation({
        onSuccess: (data) => {
            window.open(data.downloadUrl, '_blank');
        },
        onError: (error) => {
            toast.error(`Fehler beim Herunterladen: ${error.message}`);
        },
    });

    // Reopen mutation (admin/operator only)
    const reopenMutation = trpc.cashRegisters.reopenStatement.useMutation({
        onSuccess: async () => {
            toast.success('Kassensturz wurde wieder geöffnet');
            setShowReopenDialog(false);

            const freshStatement = await trpcUtils.cashRegisters.getStatement.fetch(
                { cashRegisterId: id!, date: selectedDate },
            );

            trpcUtils.cashRegisters.getStatementVersions.invalidate();
            trpcUtils.cashRegisters.getStatementOverview.invalidate();
            trpcUtils.cashRegisters.getTodayStatementStatus.invalidate();

            if (freshStatement?.latestVersion) {
                setCounts(extractCounts(freshStatement.latestVersion));
            }
            setHasPreFilled(true);
            setViewMode('new');
            setViewingVersionId(null);
        },
        onError: (error) => {
            toast.error(`Fehler beim Öffnen: ${error.message}`);
        },
    });

    const handleSave = (closeAfterSave = false) => {
        setPendingCloseAfterSave(closeAfterSave);
        if (differenceEur !== 0) {
            setShowWarningDialog(true);
            return;
        }
        openSignatureDialog();
    };

    const openSignatureDialog = () => {
        setShowWarningDialog(false);
        setShowSignatureDialog(true);
    };

    const doSave = (signatureDataUrl?: string) => {
        setShowSignatureDialog(false);
        saveMutation.mutate({
            cashRegisterId: id!,
            date: selectedDate,
            ...counts,
            note: note.trim() || undefined,
            signatureDataUrl,
            closeAfterSave: pendingCloseAfterSave,
        });
    };

    const handleDenominationChange = (key: DenominationKey, value: number) => {
        setCounts((prev) => ({ ...prev, [key]: value }));
    };

    const handleSwitchToNew = () => {
        // Pre-fill from latest version
        if (statementData?.latestVersion) {
            setCounts(extractCounts(statementData.latestVersion));
        }
        setNote('');
        setViewMode('new');
        setViewingVersionId(null);
    };

    const handleViewVersion = (versionId: string) => {
        setViewMode('viewing');
        setViewingVersionId(versionId);
    };

    const isToday = selectedDate === getTodayString();
    const isClosed = statementData?.isClosed ?? false;
    const canEdit = viewMode === 'new' && (isToday || isAdminOrOperator) && !isClosed;

    if (registerLoading) {
        return (
            <Content>
                <div className='container mx-auto py-10 max-w-5xl'>
                    <Skeleton className='h-8 w-48 mb-6' />
                    <div className='grid gap-6'>
                        <Skeleton className='h-48' />
                        <Skeleton className='h-64' />
                    </div>
                </div>
            </Content>
        );
    }

    if (!cashRegister) {
        return (
            <Content>
                <div className='container mx-auto py-10'>
                    <div className='text-center py-12'>
                        <h2 className='text-xl font-semibold mb-2'>Kasse nicht gefunden</h2>
                        <Button variant='outline' onClick={() => navigate('/cash-registers')}>
                            <ArrowLeft className='mr-2 h-4 w-4' />
                            Zurück zu Kassen
                        </Button>
                    </div>
                </div>
            </Content>
        );
    }

    return (
        <Content>
            <div className='container mx-auto py-10 max-w-5xl'>
                {/* Header */}
                <div className='flex items-center gap-4 mb-6'>
                    <Button
                        variant='ghost'
                        size='sm'
                        onClick={() => navigate(isAdminOrOperator ? `/cash-register/${id}` : '/cash-registers')}
                    >
                        <ArrowLeft className='h-4 w-4' />
                    </Button>
                    <div className='flex-1'>
                        <div className='flex items-center gap-3'>
                            <h1 className='text-2xl font-bold'>Kassensturz</h1>
                            <Badge variant='outline'>{cashRegister.name}</Badge>
                        </div>
                        <p className='text-sm text-muted-foreground'>
                            {cashRegister.store.name}
                        </p>
                    </div>

                    {/* Date Picker -- admin/op only for past dates */}
                    {isAdminOrOperator ? (
                        <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                            <PopoverTrigger asChild>
                                <Button variant='outline' className='min-w-[180px] justify-start'>
                                    <CalendarIcon className='mr-2 h-4 w-4' />
                                    {format(parse(selectedDate, 'yyyy-MM-dd', new Date()), 'dd. MMMM yyyy', { locale: de })}
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className='w-auto p-0' align='end'>
                                <Calendar
                                    mode='single'
                                    locale={de}
                                    selected={parse(selectedDate, 'yyyy-MM-dd', new Date())}
                                    onSelect={(day) => {
                                        if (day) {
                                            setSelectedDate(format(day, 'yyyy-MM-dd'));
                                            setDatePickerOpen(false);
                                        }
                                    }}
                                    disabled={(date) => isFuture(startOfDay(date))}
                                />
                            </PopoverContent>
                        </Popover>
                    ) : (
                        <Badge variant='secondary' className='text-sm px-3 py-1.5'>
                            {format(parse(selectedDate, 'yyyy-MM-dd', new Date()), 'dd. MMMM yyyy', { locale: de })}
                        </Badge>
                    )}
                </div>

                {statementLoading ? (
                    <div className='grid gap-6'>
                        <Skeleton className='h-64' />
                        <Skeleton className='h-48' />
                    </div>
                ) : (
                    <div className='space-y-6'>
                        {/* Closed banner */}
                        {isClosed && (
                            <div className='flex items-center justify-between gap-4 rounded-lg border border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950/30 px-4 py-3'>
                                <div className='flex items-center gap-3'>
                                    <Lock className='h-5 w-5 text-green-600 dark:text-green-400 shrink-0' />
                                    <div>
                                        <p className='text-sm font-medium text-green-800 dark:text-green-200'>
                                            Tagesabschluss durchgeführt
                                        </p>
                                        <p className='text-xs text-green-600 dark:text-green-400'>
                                            {statementData?.closedByUser
                                                ? `Abgeschlossen von ${statementData.closedByUser.firstName} ${statementData.closedByUser.lastName}`
                                                : 'Abgeschlossen'}
                                            {statementData?.closedAt && (
                                                <> am {format(new Date(statementData.closedAt), "dd.MM.yyyy 'um' HH:mm", { locale: de })}</>
                                            )}
                                        </p>
                                    </div>
                                </div>
                                {isAdminOrOperator ? (
                                    <Button
                                        variant='outline'
                                        size='sm'
                                        onClick={() => setShowReopenDialog(true)}
                                    >
                                        <LockOpen className='mr-2 h-3.5 w-3.5' />
                                        Wieder öffnen
                                    </Button>
                                ) : (
                                    <p className='text-xs text-muted-foreground shrink-0'>
                                        Nur Administratoren können den Abschluss wieder öffnen.
                                    </p>
                                )}
                            </div>
                        )}

                        {/* Version selector (admin/op only) */}
                        {isAdminOrOperator && versions && versions.length > 0 && (
                            <Card>
                                <CardHeader className='pb-3'>
                                    <CardTitle className='text-base'>Version</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className='flex items-center gap-3'>
                                        <Select
                                            value={viewMode === 'new' ? '__new__' : (viewingVersionId ?? '')}
                                            onValueChange={(val) => {
                                                if (val === '__new__') {
                                                    handleSwitchToNew();
                                                } else {
                                                    handleViewVersion(val);
                                                }
                                            }}
                                        >
                                            <SelectTrigger className='flex-1'>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {!isClosed && (
                                                    <SelectItem value='__new__'>
                                                        <span className='flex items-center gap-2'>
                                                            <Plus className='h-3.5 w-3.5' />
                                                            Neue Version erstellen
                                                        </span>
                                                    </SelectItem>
                                                )}
                                                {versions.map((v, index) => (
                                                    <SelectItem key={v.id} value={v.id}>
                                                        {format(new Date(v.createdAt), 'dd.MM.yyyy HH:mm:ss', { locale: de })}
                                                        {' — '}
                                                        {v.createdByUser
                                                            ? `${v.createdByUser.firstName} ${v.createdByUser.lastName}`
                                                            : 'Unbekannt'}
                                                        {index === 0 ? ' (Aktuell)' : ''}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    {viewMode === 'viewing' && (
                                        <p className='text-xs text-muted-foreground mt-2'>
                                            Gespeicherte Versionen sind schreibgeschützt und können nicht bearbeitet werden.
                                        </p>
                                    )}
                                </CardContent>
                            </Card>
                        )}

                        {/* Info bar for existing version (non-admin view) */}
                        {!isAdminOrOperator && statementData?.latestVersion && (
                            <div className='flex items-center gap-2 text-sm text-muted-foreground bg-muted/50 rounded-lg px-4 py-2'>
                                <span>
                                    Letzte Version von{' '}
                                    <strong>
                                        {statementData.latestVersion.createdByUser
                                            ? `${statementData.latestVersion.createdByUser.firstName} ${statementData.latestVersion.createdByUser.lastName}`
                                            : 'Unbekannt'}
                                    </strong>
                                    {' '}am{' '}
                                    {format(new Date(statementData.latestVersion.createdAt), "dd.MM.yyyy 'um' HH:mm", { locale: de })}
                                </span>
                            </div>
                        )}

                        {/* Viewing version info bar (admin) */}
                        {viewMode === 'viewing' && viewingVersion && (
                            <div className='flex items-center gap-2 text-sm bg-muted/50 rounded-lg px-4 py-2'>
                                <Badge variant='secondary' className='text-xs'>Schreibgeschützt</Badge>
                                <span className='text-muted-foreground'>
                                    Von{' '}
                                    <strong>
                                        {viewingVersion.createdByUser
                                            ? `${viewingVersion.createdByUser.firstName} ${viewingVersion.createdByUser.lastName}`
                                            : 'Unbekannt'}
                                    </strong>
                                    {' '}am{' '}
                                    {format(new Date(viewingVersion.createdAt), "dd.MM.yyyy 'um' HH:mm:ss", { locale: de })}
                                </span>
                            </div>
                        )}

                        {/* Zählprotokoll */}
                        <Card>
                            <CardHeader>
                                <CardTitle>Zählprotokoll</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <DenominationGrid
                                    counts={displayCounts}
                                    onChange={handleDenominationChange}
                                    disabled={viewMode === 'viewing' || isClosed}
                                />
                                <Separator className='my-6' />
                                <div className='flex justify-between items-center'>
                                    <span className='text-lg font-semibold'>Gezählter Betrag</span>
                                    <span className='text-2xl font-bold font-mono'>
                                        {formatCurrency(totalCountedEur)}
                                    </span>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Summary */}
                        <Card>
                            <CardHeader>
                                <CardTitle>Zusammenfassung</CardTitle>
                            </CardHeader>
                            <CardContent className='space-y-4'>
                                <div className='flex justify-between items-center'>
                                    <span className='text-muted-foreground'>Gezählter Betrag</span>
                                    <span className='font-mono font-medium text-lg'>
                                        {formatCurrency(totalCountedEur)}
                                    </span>
                                </div>
                                <div className='flex justify-between items-center'>
                                    <span className='text-muted-foreground'>Erwarteter Kassenstand</span>
                                    <span className='font-mono font-medium text-lg'>
                                        {formatCurrency(expectedBalanceEur)}
                                    </span>
                                </div>
                                <Separator />
                                <div className='flex justify-between items-center'>
                                    <span className='font-semibold text-lg'>Differenz</span>
                                    <span className={`font-mono font-bold text-xl ${differenceEur === 0 ? 'text-green-600' : 'text-destructive'}`}>
                                        {differenceEur > 0 ? '+ ' : differenceEur < 0 ? '− ' : ''}{formatCurrency(Math.abs(differenceEur))}
                                    </span>
                                </div>
                                {differenceEur !== 0 && (
                                    <p className='text-sm text-destructive'>
                                        {differenceEur > 0
                                            ? 'Es befindet sich mehr Geld in der Kasse als erwartet.'
                                            : 'Es befindet sich weniger Geld in der Kasse als erwartet.'}
                                    </p>
                                )}
                            </CardContent>
                        </Card>

                        {/* Notes */}
                        {viewMode === 'new' && !isClosed ? (
                            <Card>
                                <CardHeader>
                                    <CardTitle>Notiz</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <Textarea
                                        placeholder='Optionale Notiz zum Kassensturz...'
                                        value={note}
                                        onChange={(e) => setNote(e.target.value)}
                                        rows={3}
                                    />
                                </CardContent>
                            </Card>
                        ) : (viewingVersion?.note || (isClosed && statementData?.latestVersion?.note)) ? (
                            <Card>
                                <CardHeader>
                                    <CardTitle>Notiz</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <p className='text-sm text-muted-foreground whitespace-pre-wrap'>
                                        {viewingVersion?.note ?? statementData?.latestVersion?.note}
                                    </p>
                                </CardContent>
                            </Card>
                        ) : null}

                        {/* Download Buttons (when PDFs are available) */}
                        {statementData?.latestVersion?.hasKassenbericht && statementData?.latestVersion?.hasAnlage && (
                            <Card>
                                <CardHeader>
                                    <CardTitle>Berichte herunterladen</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className='flex flex-wrap gap-3'>
                                        <Button
                                            variant='outline'
                                            onClick={() => {
                                                if (statementData.latestVersion?.id) {
                                                    kassenberichtDownload.mutate({ versionId: statementData.latestVersion.id });
                                                }
                                            }}
                                            disabled={kassenberichtDownload.isPending}
                                        >
                                            {kassenberichtDownload.isPending ? (
                                                <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                                            ) : (
                                                <Download className='mr-2 h-4 w-4' />
                                            )}
                                            Kassenbericht (Tagesabschluss)
                                        </Button>
                                        <Button
                                            variant='outline'
                                            onClick={() => {
                                                if (statementData.latestVersion?.id) {
                                                    anlageDownload.mutate({ versionId: statementData.latestVersion.id });
                                                }
                                            }}
                                            disabled={anlageDownload.isPending}
                                        >
                                            {anlageDownload.isPending ? (
                                                <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                                            ) : (
                                                <Download className='mr-2 h-4 w-4' />
                                            )}
                                            Anlage (Zählprotokoll)
                                        </Button>
                                    </div>
                                    {statementData.latestVersion?.cashStatementNumber && (
                                        <p className='text-xs text-muted-foreground mt-2'>
                                            Kassenbericht Nr. {statementData.latestVersion.cashStatementNumber}
                                        </p>
                                    )}
                                </CardContent>
                            </Card>
                        )}

                        {/* Save Buttons (only in new mode and when allowed) */}
                        {viewMode === 'new' && canEdit && (
                            <div className='flex justify-end gap-3'>
                                <Button
                                    size='lg'
                                    variant='outline'
                                    onClick={() => handleSave(false)}
                                    disabled={saveMutation.isPending}
                                >
                                    {saveMutation.isPending && !pendingCloseAfterSave ? (
                                        <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                                    ) : (
                                        <Save className='mr-2 h-4 w-4' />
                                    )}
                                    Speichern
                                </Button>
                                <Button
                                    size='lg'
                                    onClick={() => handleSave(true)}
                                    disabled={saveMutation.isPending}
                                >
                                    {saveMutation.isPending && pendingCloseAfterSave ? (
                                        <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                                    ) : (
                                        <Lock className='mr-2 h-4 w-4' />
                                    )}
                                    Speichern & Tagesabschluss
                                </Button>
                            </div>
                        )}

                        {viewMode === 'new' && !canEdit && !isClosed && (
                            <p className='text-sm text-muted-foreground text-center'>
                                Sie können den Kassensturz nur für den heutigen Tag erstellen. Nur Administratoren und Operatoren können vergangene Tage bearbeiten.
                            </p>
                        )}
                    </div>
                )}
            </div>

            {/* Warning Dialog */}
            <AlertDialog open={showWarningDialog} onOpenChange={setShowWarningDialog}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Differenz vorhanden</AlertDialogTitle>
                        <AlertDialogDescription>
                            Der gezählte Betrag weicht um{' '}
                            <strong className='text-destructive'>{formatCurrency(Math.abs(differenceEur))}</strong>{' '}
                            vom erwarteten Kassenstand ab.
                            {differenceEur > 0
                                ? ' Es befindet sich mehr Geld in der Kasse als erwartet.'
                                : ' Es befindet sich weniger Geld in der Kasse als erwartet.'}
                            <br /><br />
                            Möchten Sie den Kassensturz trotzdem speichern?
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                        <AlertDialogAction onClick={openSignatureDialog}>
                            Trotzdem speichern
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Reopen Dialog (admin/operator only) */}
            <AlertDialog open={showReopenDialog} onOpenChange={setShowReopenDialog}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Tagesabschluss wieder öffnen?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Der Kassensturz für diesen Tag wurde bereits abgeschlossen. Wenn Sie ihn wieder öffnen, können Benutzer neue Versionen erstellen. Der Tagesabschluss muss anschließend erneut durchgeführt werden.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={reopenMutation.isPending}>Abbrechen</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => {
                                reopenMutation.mutate({
                                    cashRegisterId: id!,
                                    date: selectedDate,
                                });
                            }}
                            disabled={reopenMutation.isPending}
                        >
                            {reopenMutation.isPending && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
                            Wieder öffnen
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Signature Dialog */}
            <SignatureDialog
                open={showSignatureDialog}
                onOpenChange={setShowSignatureDialog}
                onConfirm={(signatureDataUrl) => doSave(signatureDataUrl)}
                isLoading={saveMutation.isPending}
            />
        </Content>
    );
};

export default CashRegisterStatement;
